import { IPromotions } from '@llws/lift-entity-interfaces';
import { IQueryCommandOptions, Equal, Not } from '@llws/dynamic-router-utils';
import { IModifyPayload } from '@/shared';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import promotions from '@/shared/services/promotions';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IPromotionLocation } from '@/views/properties/promotions/Index.vue';

export type TPromotionsWithPropertyCount = IPromotions & { countBuildingsNotDeleted?: number };

// Base store configuration
const adminGatewayResourcePath: string = '/property-api/promotions';
const listViewName: string = 'summary';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {
  alias: 'promotion',
  relations: [
    {
      path: 'buildings',
      alias: 'buildings',
      whereCommands: [
        Not(Equal('status', 'deleted')),
      ],
    },
    {
      path: 'promotionsCities',
      alias: 'promotionsCities',
    },
    {
      path: 'promotionsCategory',
      alias: 'promotionsCategory',
    },
  ],
  aggregations: [
    {
      propertyAlias: 'countBuildingsNotDeleted',
      table: 'promotions_buildings',
      whereCommands: [
        Not(Equal('building.status', 'deleted')),
        Equal('promotionId', 'promotion.id'),
      ],
      relations: [{ path: 'building', alias: 'building' }],
      aggregate: 'COUNT',
      column: 'id',
    },
  ],
  where: [
    Not(Equal('status', 'deleted')),
  ],
  select: [
    'buildings.id',
    'buildings.status',
    'buildings.buildingName',
    'buildings.cityId',
    'countBuildingsNotDeleted',
  ],
};

const defaultGetSingleFilters: IQueryCommandOptions = {
  relations: [
    {
      path: 'buildings',
      alias: 'b',
      whereCommands: [
        Not(Equal('status', 'deleted')),
      ],
    },
    {
      path: 'promotionsCategory',
      alias: 'promotionsCategory',
    },
  ],
};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<TPromotionsWithPropertyCount>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

// Extend StoreBaseImplementation object to add custom state/getters/mutations/actions.
export default {
  namespaced: base.namespaced, // 'promotions/'

  state: {
    ...base.state,

    // custom state
    ...{},
  },

  getters: {
    ...base.getters,

    // custom getters
    ...{},
  },

  mutations: {
    ...base.mutations,

    // custom mutations
    ...{
      setNewSingle(state: IStateStrict<IPromotions>, data?: Record<string, any>) {
        state.single = {
          data: {
            ...data && data,
            id: undefined,
            title: '',
            promotionsCategoryId: null,
            numOfMonths: null,
            leaseLength: null,
            percentageDiscount: null,
            discountAmount: null,
            valueAmount: null,
            details: '',
            start: '',
            end: '',
            orderBy: state.list?.data?.length || 0,
            status: 'enabled',
            buildings: [],
            created: new Date(),
          } as any,
          meta: null,
        };
      },
      setDefinedSingle(state: IStateStrict<IPromotions>, data?: Record<string, any>) {
        state.single = data;
      },
    },
  },

  actions: {
    ...base.actions,

    // custom actions
    ...{
      savePropertiesToPromotion: async (ctx: IAction<IStateStrict<IPromotions>>, payload: IModifyPayload<number, IPromotionLocation[]>) => {
        const { id } = payload;
        const data: number[] = payload.data.map((b) => b.id ?? 0);
        const result = await promotions.updateProperties(id!, data);
        const { list, single } = ctx.getters;
        const idx = list.findIndex((p: IPromotions) => p.id === id);

        if (single && single.id === id) {
          single.buildings = result.meta?.inverseRecords;
          ctx.commit('set', single);
        }

        if (list[idx]) {
          list[idx].countBuildingsNotDeleted = `${(result.meta?.inverseRecords ?? []).length}`;
          ctx.commit('set', list);
        }
      },
    },
  },
};
